import React, { useRef, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import RunTestFooter from './RunTestFooter';
import { useHttpClient } from '../../hooks/http-hook';
import { PageType, RUN_TEST_URL, Test } from './RunTest';
import { FieldArray, Formik } from 'formik';
import { runSingleTestSchema } from './RunSingleTestSchema';
import RunTestRow from './RunTestRow';
import RunTestAddRow from './RunTestAddRow';
import { useAppSelector } from '../../services/store';
import { useAuth } from '../../hooks/useAuth';
import wait from '../../utils/wait';
import { EmailsBoxHandle } from '../EmailsBox/EmailsBox';

interface RunTestSingleProps {
  onClose: () => void;
  setShowSuccessInfo: React.Dispatch<React.SetStateAction<boolean>>;
  setTestType: React.Dispatch<React.SetStateAction<string>>;
  handleCreate: (values: any, setValues: any) => void;
  handleDelete: (values: any, setValues: any, index: number) => void;
  pageTypes: PageType[];
}

export interface RunTestSingleInitialValues {
  tests: Test[];
}

const RunTestSingle = ({
  onClose,
  setShowSuccessInfo,
  handleCreate,
  handleDelete,
  pageTypes,
  setTestType
}: RunTestSingleProps) => {
  const { isSuperAdmin } = useAuth();
  const [runTestEmails, setRunTestEmails] = useState<string[]>([]);
  const emailsBoxRef = useRef<EmailsBoxHandle>(null);

  const userId = useAppSelector((store) => store.user.userId);

  const { sendRequest } = useHttpClient();

  const initialValues = {
    tests: [
      {
        name: '',
        pageType: '',
        label: ''
      }
    ]
  } as RunTestSingleInitialValues;

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={async (values) => {
          const emails = emailsBoxRef.current?.flush();
          if (!emails) return;

          const data = {
            emails,
            links: values.tests.map((item: Test) => ({
              name: item.name.replace('https://', ''),
              pageType: item.pageType,
              label: item.label
            })),
            user: userId
          };

          try {
            await sendRequest(RUN_TEST_URL, 'POST', JSON.stringify(data), {
              'Content-Type': 'application/json'
            });
            setShowSuccessInfo(true);
            setTestType('singleTestType');
          } catch (err) {
            console.error(err);
            if (err && typeof err === 'object' && 'message' in err && err.message === 'Too many requests') {
              enqueueSnackbar('You were blocked by test rate limiter', { variant: 'error' });
              await wait(2000);
              window.location.reload();
            }
          }
        }}
        validationSchema={runSingleTestSchema(isSuperAdmin)}
      >
        {(props) => {
          const { values, handleChange, handleSubmit, setValues, isSubmitting } = props;

          return (
            <>
              <form onSubmit={handleSubmit} className={'run-test-form'}>
                <div className="links-container">
                  <FieldArray name="tests">
                    {() =>
                      values.tests.map((test, index) => (
                        <RunTestRow
                          pageTypes={pageTypes}
                          key={index}
                          index={index}
                          test={test}
                          values={values}
                          setValues={setValues}
                          handleChange={handleChange}
                          handleDelete={handleDelete}
                          minOneTest
                        />
                      ))
                    }
                  </FieldArray>
                </div>
                <RunTestAddRow
                  disabled={values.tests.length >= 10}
                  handleCreate={() => handleCreate(values, setValues)}
                />
                <RunTestFooter
                  onClose={onClose}
                  runTestEmails={runTestEmails}
                  setRunTestEmails={setRunTestEmails}
                  emailsBoxRef={emailsBoxRef}
                  loading={isSubmitting}
                />
              </form>
            </>
          );
        }}
      </Formik>
    </>
  );
};

export default RunTestSingle;
